import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { UserPersona } from '../services/userService';
import useAppState from '../common/AppState';

// Define the context type
interface UserContextType {
  activePersona: UserPersona;
  setActivePersona: (persona: UserPersona) => void;
  allPersonas: UserPersona[];
}

// Create the context with a default value
const UserContext = createContext<UserContextType | undefined>(undefined);

// Default persona when no user is logged in
const defaultPersona: UserPersona = {
  id: 'default',
  name: 'Guest User',
  email: 'guest@example.com',
  role: 'Loan Advisor',
  avatar: '/avatars/default_avatar.png',
  title: 'Guest',
  description: 'Not logged in',
};

// Provider component
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { profile, orgUser } = useAppState();

  // Build the active persona from real profile data
  const activePersona: UserPersona = useMemo(() => {
    if (!profile || !orgUser) {
      console.log('👤 UserContext: No profile/orgUser, using default persona');
      return defaultPersona;
    }

    console.log('👤 UserContext: Building persona from real profile', { profile, orgUser });

    // Map org_users role to UserRole type
    const roleMap: Record<string, UserPersona['role']> = {
      admin: 'CEO',
      analyst: 'VP of Lending',
      viewer: 'Loan Advisor',
      auditor: 'Loan Advisor',
    };

    const mappedRole = roleMap[orgUser.role] || 'Loan Advisor';

    return {
      id: profile.user_id,
      name: `${orgUser.first_name} ${orgUser.last_name}`,
      email: orgUser.email,
      role: mappedRole,
      avatar: profile.user_metadata?.avatarUrl || '/avatars/default_avatar.png',
      title: orgUser.role.charAt(0).toUpperCase() + orgUser.role.slice(1),
      description: orgUser.org_name || 'Organization',
    };
  }, [profile, orgUser]);

  // setActivePersona is a no-op now - we use the logged-in user
  const setActivePersona = (_persona: UserPersona) => {
    console.log('👤 UserContext: setActivePersona called but ignored - using logged-in user');
  };

  // allPersonas now only contains the current user
  const allPersonas = useMemo(() => [activePersona], [activePersona]);

  // Provide the context value
  const contextValue = {
    activePersona,
    setActivePersona,
    allPersonas,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};